import { useCallback, useEffect, useState } from 'react';
// import { NavLink, generatePath } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  // IconVisa,
  // IconMasterCard,
  Popup,
  formatBalance,
  InputText,
  ButtonPseudo,
  ButtonPrimary,
  ButtonSecondary,
  ButtonSize,
  IconSettings,
  SelectItemProps,
  IconSelected,
  IconBlock,
  IconEye,
  IconEyeOff,
  IconDownload,
  SidePanel,
  SidePanelType,
  Checkbox,
  ToggleInput,
  ResponseError,
  PermissionEnum,
  ChangeLogs,
  PermissionDenied,
  DateInput,
  formatDate,
  IconLoader,
  IconSelectedCircle,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import AccountBalance from '../../partial/balance/balance';
import Card from '../../partial/card/card';
import { CustomerStatus } from '../../../hoc/customers/types';
import {
  clearSettingsPanel,
  clearClosingBalances,
} from '../../../hoc/corporate/store/slices/accounts/accounts';
import {
  useUpdateCustomersOverdraftStatusMutation,
  useLazyGetCustomersAccountsChangeLogsQuery,
} from '../../../hoc/customers/store/services/customers';
import { useLazyGetCorporateAccountClosingBalancesQuery } from '../../../hoc/corporate/store/services/corporate';
import { AccountInfoTypes } from '../types';

// import ROUTES from '../../../routes';

import cn from './accounts-content.module.css';

const AccountInfo = ({
  info,
  balance,
  onBlockTopup,
  onBlockWithdraw,
  blockTopupInProgress,
  onClose,
  blockWithdrawInProgress,
  closeInProgress,
  setUserAccount,
  setTopUpPanelIsOpen,
  closeIsSuccess,
  blockDebitIsSuccess,
  blockCreditIsSuccess,
  closeOpenError,
  blockUblockTopupError,
  blockUblockWithdrawalsError,
  setShowCurrencies,
  setSelectedAccountCurrenciesId,
  setStatementPanelVisible,
  setSelectedAccount,
  setCurrenciesList,
  unblockDebitIsSuccess,
  unblockCreditIsSucceess,
  openIsSuccess,
  onUpdateVisibility,
  updateVisibilityInProgress,
  overdraftEnabled,
  accountsBalances,
  setBalances,
  defaultBalances,
  setDefaultBalances,
  onUpdateOverdraftBalanceValue,
  updateOverdraftBalanceIsLoading,
  customerId,
  status: organizationStatus,
  closedBalances,
}: AccountInfoTypes) => {
  const [blockWithdrawPopupVisible, setBlockWithdrawPopupVisible] =
    useState(false);
  const [blockTopupPopupVisible, setBlockTopupPopupVisible] = useState(false);
  const [closePopupVisible, setClosePopupVisible] = useState(false);
  const [creditBlock, setCreditBlock] = useState(false);
  const [debitBlock, setDebitBlock] = useState(false);
  const [closed, setClosed] = useState(false);
  const [comment, setComment] = useState('');
  const [settingsPanelVisible, setSettingsPanelVisible] = useState(false);
  const [visibleToClient, setVisibleToClient] = useState(false);
  const [selectedTab, setSelectedTab] = useState('settings');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountOverdraftEnabled, setOverdraftEnabled] = useState(false);
  const [closingBalancesAt, setClosingBalancesAt] = useState('');

  const {
    customersDictionaries: {
      currencies: currenciesList,
      baseCurrency,
      currenciesList: currenciesDictionaries,
      activePermissions,
    },
    corporateCustomers: {
      info: {
        accounts: { accountsLogs, accountsLogsPagination },
      },
    },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { ffSettings } = useAppSelector((state) => state.root);

  const dispatch = useAppDispatch();

  const [updateOverdraftStatus, { isLoading: overdraftUpdateInProgress }] =
    useUpdateCustomersOverdraftStatusMutation();
  const [
    getCorporateCustomerClosingBalances,
    { isLoading: balancesIsLoading, isFetching: balancesIsFetching },
  ] = useLazyGetCorporateAccountClosingBalancesQuery();

  const [getAccountsLogs, { isLoading, isFetching }] =
    useLazyGetCustomersAccountsChangeLogsQuery();

  const getCorporateCustomerClosingBalancesAction = async ({
    accountId,
    date,
  }: {
    accountId: string;
    date?: string;
  }) => {
    const body: {
      id: string;
      closingBalancesAt?: string;
    } = {
      id: accountId,
    };

    if (date) {
      body.closingBalancesAt = date;
    }

    await getCorporateCustomerClosingBalances(
      `?accountId=${body.id}&date=${body.closingBalancesAt}`,
    );
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getAccountsLogs(
        `?take=20&entityType=ACCOUNT&entityId=${selectedAccountId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [selectedAccountId],
  );

  const onUpdateOverdraftStatus = async ({
    enabled,
    accountId,
  }: {
    enabled: boolean;
    accountId: string;
  }) => {
    setOverdraftEnabled(enabled);

    const response = (await updateOverdraftStatus({
      id: customerId as string,
      accountId,
      overdraftEnabled: enabled,
    })) as { error?: ResponseError };

    if (response.error) {
      setOverdraftEnabled(!enabled);
    }
  };

  useEffect(() => {
    if (blockCreditIsSuccess || unblockCreditIsSucceess) {
      setBlockTopupPopupVisible(false);
    }
  }, [blockCreditIsSuccess, unblockCreditIsSucceess]);

  useEffect(() => {
    if (blockDebitIsSuccess || unblockDebitIsSuccess) {
      setBlockWithdrawPopupVisible(false);
    }
  }, [blockDebitIsSuccess, unblockDebitIsSuccess]);

  useEffect(() => {
    if (closeIsSuccess || openIsSuccess) {
      setClosePopupVisible(false);
      setComment('');
    }
  }, [closeIsSuccess, openIsSuccess]);

  useEffect(() => {
    if (info) {
      setDebitBlock(info.debitBlock);
      setCreditBlock(info.creditBlock);
      setClosed(info.closed);
      setVisibleToClient(info.visibleToClient);
    }
  }, [info]);

  useEffect(() => {
    if (
      selectedTab === 'logs' &&
      !accountsLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      settingsPanelVisible
    ) {
      getChangeLogsAction();
    }
  }, [selectedTab, settingsPanelVisible]);

  useEffect(() => {
    if (info && info.overdraftEnabled && overdraftEnabled) {
      setOverdraftEnabled(true);
    }
  }, [info, settingsPanelVisible, overdraftEnabled]);

  useEffect(() => {
    if (balance && settingsPanelVisible && !accountsBalances) {
      setBalances(balance);

      setDefaultBalances(balance);
    }
  }, [settingsPanelVisible, balance, accountsBalances]);

  let status;

  if (info) {
    if (info.closed) {
      status = 'Account closed';
    } else if (!info.closed && info?.debitBlock && info.creditBlock) {
      status = 'Account blocked';
    } else if (!info.closed && !info?.debitBlock && info.creditBlock) {
      status = 'Blocked for top-up';
    } else if (!info.closed && info?.debitBlock && !info.creditBlock) {
      status = 'Withdrawal blocked';
    } else if (!info.closed && !info?.debitBlock && !info.creditBlock) {
      if (info?.active) {
        status = 'Active';
      } else {
        status = 'Inactive';
      }
    }
  }

  const baseCurrencyBalance = balance?.find(
    (item) => item.currencyId === baseCurrency?.id,
  );

  const balanceWithCurrencyName = balance?.map((item) => ({
    ...item,
    currencyName:
      currenciesList?.find((currency) => item.currencyId === currency.value)
        ?.suffix ||
      item?.currency?.name ||
      '',
  }));

  return (
    <Card className={cn.accountCard}>
      <div className={cn.accountItemTop}>
        <div className={cn.accountItemTitleWrapper}>
          <div className={cn.accountItemTitle}>
            {info?.fullNumber}
            <div className={cn.accountCreatedDate}>
              {`created on ${new Date(
                info?.createdAt as string,
              ).toLocaleDateString()}`}
            </div>
          </div>
          <div className={cn.accountTotal} style={{ display: 'none' }}>
            {`${formatBalance(
              info?.totalBalanceBC || 0,
              currenciesDictionaries?.find(
                (item) => item.id === baseCurrency?.id,
              ),
            )} ${baseCurrency?.name}`}
            <span className={cn.accountTotalHint}>bc</span>
          </div>
        </div>
        <div>
          <div className={cn.accountStatus}>
            {overdraftEnabled && info?.overdraftEnabled && overdraftEnabled && (
              <div className={cn.overdraftStatus}>Overdraft</div>
            )}
            <div
              className={`${cn.accountStatusValue} ${
                !info?.active ||
                info.debitBlock ||
                info.creditBlock ||
                info.closed
                  ? cn.accountStatusInactive
                  : ''
              }`}
            >
              {status === 'Active' ? (
                <div className={cn.iconWrapper}>
                  <IconSelected className={cn.statusIcon} />
                </div>
              ) : (
                <div className={cn.iconWrapper}>
                  <IconBlock className={cn.statusIcon} />
                </div>
              )}
              <span>{status}</span>
            </div>
            <div
              className={`${cn.accountStatusValue} ${
                !info?.visibleToClient ? cn.accountStatusInactive : ''
              } ${cn.accountVisibility}`}
            >
              {info?.visibleToClient ? (
                <div className={cn.iconWrapper}>
                  <IconEye width={16} height={16} className={cn.statusIcon} />
                </div>
              ) : (
                <div className={cn.iconWrapper}>
                  <IconEyeOff
                    width={16}
                    height={16}
                    className={cn.statusIcon}
                  />
                </div>
              )}
              <span>{info?.visibleToClient ? 'Visible' : 'Not Visible'}</span>
            </div>
          </div>
        </div>
      </div>
      <div className={cn.accountCurrenciesWrapper}>
        {baseCurrencyBalance && (
          <div className={cn.accountCurrency}>
            <div className={cn.accountCurrencyName}>
              {`${
                currenciesList?.find(
                  (currency) =>
                    baseCurrencyBalance.currencyId === currency.value,
                )?.suffix
              }:`}
            </div>
            {formatBalance(
              baseCurrencyBalance.value,
              currenciesDictionaries?.find(
                (item) => item.id === baseCurrencyBalance.currencyId,
              ),
            )}
            {baseCurrencyBalance?.overdraftEnabled &&
              overdraftEnabled &&
              accountOverdraftEnabled && (
                <div className={cn.overdraftValue}>
                  {formatBalance(
                    baseCurrencyBalance.overdraftValue as number,
                    currenciesDictionaries?.find(
                      (item) => item.id === baseCurrencyBalance.currencyId,
                    ),
                  )}
                </div>
              )}
          </div>
        )}
        {balanceWithCurrencyName
          ?.filter(
            (item) => item.currencyId !== baseCurrencyBalance?.currencyId,
          )
          .sort((a, b) => a?.currencyName?.localeCompare(b?.currencyName))
          .map((item) => (
            <div className={cn.accountCurrency} key={item.id}>
              <div className={cn.accountCurrencyName}>
                {`${item.currencyName}:`}
              </div>
              {formatBalance(
                item.value,
                currenciesDictionaries?.find(
                  (currency) => currency.id === item.currencyId,
                ),
              )}
              {item?.overdraftEnabled &&
                overdraftEnabled &&
                accountOverdraftEnabled && (
                  <div className={cn.overdraftValue}>
                    {formatBalance(
                      item.overdraftValue as number,
                      currenciesDictionaries?.find(
                        (currency) => currency.id === item.currencyId,
                      ),
                    )}
                  </div>
                )}
            </div>
          ))}
      </div>
      <div className={cn.accountItemBottom}>
        <ButtonSecondary
          text="Available currencies"
          size={ButtonSize.SMALL}
          className={`${cn.currenciesButton} button-white`}
          icon={<IconSettings width={16} height={16} />}
          onClick={() => {
            setShowCurrencies(true);
            setSelectedAccountCurrenciesId(info?.id);
          }}
        />

        <div className={cn.accountItemControls}>
          {info?.externalId && (
            <div className={cn.externalAccountLinked}>
              <IconSelectedCircle />
              External Account Linked
            </div>
          )}
          <ButtonPseudo
            text="Top up options"
            size={ButtonSize.SMALL}
            className={`${cn.topUpButton} ${cn.currenciesButton} button-white`}
            icon={<IconSettings width={16} height={16} />}
            onClick={() => {
              setUserAccount(info?.id);
              setTopUpPanelIsOpen(true);
            }}
          />
          <ButtonPseudo
            text="Statement"
            size={ButtonSize.SMALL}
            className={`${cn.withMargin} ${cn.downloadStatementButton} ${cn.currenciesButton} button-white`}
            icon={<IconDownload />}
            onClick={() => {
              setStatementPanelVisible(true);
              setSelectedAccount(info?.id);

              setCurrenciesList(
                balanceWithCurrencyName?.map((item) => ({
                  name: item.currencyName,
                  value: item.currencyId,
                })) as SelectItemProps[],
              );
            }}
          />
          <ButtonSecondary
            text="Settings"
            size={ButtonSize.SMALL}
            className={`${cn.currenciesButton} button-white`}
            onClick={() => {
              setSettingsPanelVisible(true);
              setSelectedAccountId(info?.id);
            }}
          />
        </div>
      </div>
      <SidePanel
        isOpen={settingsPanelVisible}
        title="Account settings"
        type={SidePanelType.Accounts}
        onClose={() => {
          setSettingsPanelVisible(false);
          setSelectedAccountId('');
          setSelectedTab('settings');
          setOverdraftEnabled(false);
          setBalances(undefined);
          setDefaultBalances(undefined);

          dispatch(clearSettingsPanel());
        }}
        sidepanelExpandedKey="corporate-account-settings"
        footer={
          <>
            {organizationStatus !== CustomerStatus.CLOSED && (
              <ButtonPrimary
                text={
                  updateVisibilityInProgress || updateOverdraftBalanceIsLoading
                    ? 'Loading...'
                    : 'Save'
                }
                onClick={() => {
                  if (info?.visibleToClient !== visibleToClient) {
                    onUpdateVisibility({
                      id: info?.id,
                      visibleToClientValue: visibleToClient,
                    });
                  }

                  if (!isEqual(defaultBalances, accountsBalances)) {
                    onUpdateOverdraftBalanceValue(info?.id);
                  }
                }}
                disabled={
                  isEqual(defaultBalances, accountsBalances) &&
                  info?.visibleToClient === visibleToClient
                }
                bold
              />
            )}

            <ButtonSecondary
              text="Cancel"
              onClick={() => {
                setSettingsPanelVisible(false);
                setSelectedAccountId('');
                setSelectedTab('settings');
                setOverdraftEnabled(false);
                setBalances(undefined);
                setDefaultBalances(undefined);

                dispatch(clearSettingsPanel());
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        <div className={cn.accountSettingsContent}>
          <div className={cn.accountTabs}>
            <button
              type="button"
              className={`${cn.accountTab} ${
                selectedTab === 'settings' ? cn.active : ''
              }`}
              onClick={() => setSelectedTab('settings')}
            >
              Settings
            </button>
            <button
              type="button"
              className={`${cn.accountTab} ${
                selectedTab === 'logs' ? cn.active : ''
              }`}
              onClick={() => setSelectedTab('logs')}
            >
              Logs
            </button>
          </div>
          {selectedTab === 'settings' && (
            <>
              <div className={cn.accountSettingsTitle}>Account blocking</div>
              <div className={cn.blockAccountWrapper}>
                <Checkbox
                  type="checkbox"
                  name={`creditBlock-${info.id}`}
                  checked={creditBlock}
                  onChange={(e) => {
                    setCreditBlock(e.target.checked);

                    setBlockTopupPopupVisible(true);
                  }}
                  title="Block top-up"
                  disabled={organizationStatus === CustomerStatus.CLOSED}
                />
              </div>
              <div className={cn.blockAccountWrapper}>
                <Checkbox
                  type="checkbox"
                  name={`debitBlock-${info.id}`}
                  checked={debitBlock}
                  onChange={(e) => {
                    setDebitBlock(e.target.checked);

                    setBlockWithdrawPopupVisible(true);
                  }}
                  title="Block withdraw"
                  disabled={organizationStatus === CustomerStatus.CLOSED}
                />
              </div>
              <div className={cn.accountSettingsTitle}>Account visibility</div>
              <div className={cn.blockAccountWrapper}>
                <Checkbox
                  type="radio"
                  name={`visibleToClient-${info.id}`}
                  checked={visibleToClient}
                  onChange={() => setVisibleToClient(true)}
                  title="Visible to the client"
                  disabled={organizationStatus === CustomerStatus.CLOSED}
                />
              </div>
              <div className={cn.blockAccountWrapper}>
                <Checkbox
                  type="radio"
                  name={`notVisibleToClient-${info.id}`}
                  checked={!visibleToClient}
                  onChange={() => setVisibleToClient(false)}
                  title="Not visible to the client"
                  disabled={organizationStatus === CustomerStatus.CLOSED}
                />
              </div>
              <div className={cn.balances}>
                <div className={cn.closingBalanceWrapper}>
                  <DateInput
                    placeholder="Select balance date"
                    value={closingBalancesAt}
                    onChange={(e) => {
                      setClosingBalancesAt(new Date(e).toISOString());

                      getCorporateCustomerClosingBalancesAction({
                        accountId: info?.id,
                        date: new Date(e).toISOString(),
                      });
                    }}
                    onReset={() => {
                      setClosingBalancesAt('');

                      dispatch(clearClosingBalances());
                    }}
                    withoutCustomSelector
                  />
                </div>

                <div
                  className={`${cn.balancesTop} ${
                    closedBalances ? cn.flexStart : ''
                  }`}
                >
                  <div className={cn.balancesTitle}>Balance</div>
                  {closedBalances && (
                    <div className={cn.balancesTitle}>{`Balance on ${formatDate(
                      closingBalancesAt,
                    )}`}</div>
                  )}
                  {overdraftEnabled && (
                    <div className={cn.overdraftToggleWrapper}>
                      <ToggleInput
                        name={`accountOverdraftEnabled-${info.id}`}
                        checked={accountOverdraftEnabled}
                        title="Overdraft"
                        onChange={(e) =>
                          onUpdateOverdraftStatus({
                            enabled: e.target.checked,
                            accountId: info?.id,
                          })
                        }
                        disabled={
                          overdraftUpdateInProgress ||
                          organizationStatus === CustomerStatus.CLOSED
                        }
                      />
                    </div>
                  )}
                </div>
                <div className={cn.balancesWrapper}>
                  {(balancesIsLoading || balancesIsFetching) && (
                    <div className={cn.closingBalancesLoader}>
                      <IconLoader />
                    </div>
                  )}
                  {baseCurrencyBalance && (
                    <AccountBalance
                      balance={{
                        ...baseCurrencyBalance,
                        currencyName: currenciesList?.find(
                          (currency) =>
                            currency.value === baseCurrencyBalance.currencyId,
                        )?.suffix,
                      }}
                      currenciesList={currenciesDictionaries}
                      overdraftEnabled={
                        overdraftEnabled && accountOverdraftEnabled
                      }
                      balances={accountsBalances}
                      setBalances={setBalances}
                      updateOverdraftBalanceIsLoading={
                        updateOverdraftBalanceIsLoading
                      }
                      defaultBalance={defaultBalances?.find(
                        (balanceValue) =>
                          balanceValue.id === baseCurrencyBalance.id,
                      )}
                      closedBalance={
                        closedBalances
                          ?.find(
                            (item) =>
                              item.currencyId ===
                              baseCurrencyBalance.currencyId,
                          )
                          ?.value?.toString()
                          ? formatBalance(
                              closedBalances?.find(
                                (item) =>
                                  item.currencyId ===
                                  baseCurrencyBalance.currencyId,
                              )?.value as number,
                              currenciesDictionaries?.find(
                                (item) =>
                                  item.id === baseCurrencyBalance.currencyId,
                              ),
                            )
                          : undefined
                      }
                    />
                  )}
                  {balanceWithCurrencyName
                    ?.filter(
                      (item) =>
                        item.currencyId !== baseCurrencyBalance?.currencyId,
                    )
                    .sort((a, b) =>
                      a?.currencyName?.localeCompare(b?.currencyName),
                    )
                    .map((item) => (
                      <AccountBalance
                        key={item.id}
                        balance={item}
                        currenciesList={currenciesDictionaries}
                        overdraftEnabled={
                          overdraftEnabled && accountOverdraftEnabled
                        }
                        balances={accountsBalances}
                        setBalances={setBalances}
                        updateOverdraftBalanceIsLoading={
                          updateOverdraftBalanceIsLoading
                        }
                        defaultBalance={defaultBalances?.find(
                          (balanceValue) => balanceValue.id === item.id,
                        )}
                        closedBalance={
                          closedBalances
                            ?.find(
                              (balanceValue) =>
                                balanceValue.currencyId === item.currencyId,
                            )
                            ?.value?.toString()
                            ? formatBalance(
                                closedBalances?.find(
                                  (balanceValue) =>
                                    balanceValue.currencyId === item.currencyId,
                                )?.value as number,
                                currenciesDictionaries?.find(
                                  (currency) => currency.id === item.currencyId,
                                ),
                              )
                            : undefined
                        }
                      />
                    ))}
                </div>
              </div>
              {organizationStatus !== CustomerStatus.CLOSED &&
                (!closed ||
                  (closed && !ffSettings?.ffAccountReopenDisabled)) && (
                  <div className={cn.closeAccountWrapper}>
                    <button
                      type="button"
                      className={`${cn.closeAccountButton} ${
                        closed ? cn.openAccountButton : ''
                      }`}
                      onClick={() => setClosePopupVisible(true)}
                    >
                      {`${closed ? 'Open' : 'Close'} account`}
                    </button>
                  </div>
                )}
            </>
          )}
          {selectedTab === 'logs' && (
            <>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <ChangeLogs
                  entityType="ACCOUNT"
                  entityId={selectedAccountId}
                  changeLogsIsLoading={isLoading}
                  changeLogs={accountsLogs}
                  activePermission={activePermissions}
                  users={users}
                  pagination={accountsLogsPagination}
                  changeLogsIsFetching={isFetching}
                  getChangeLogs={getChangeLogsAction}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </>
          )}
        </div>
      </SidePanel>
      <Popup
        title={`${!debitBlock ? 'Unblock' : 'Block'} all withdrawals?`}
        onCancelClick={() => setBlockWithdrawPopupVisible(false)}
        onOkClick={() =>
          onBlockWithdraw({
            id: info?.id,
            debitBlock: !info?.debitBlock,
          })
        }
        cancelButtonText="Cancel"
        okButtonText={
          blockWithdrawInProgress
            ? 'Loading...'
            : `Yes, ${!debitBlock ? 'Unblock' : 'Block'}`
        }
        isVisible={blockWithdrawPopupVisible}
        parent={document.getElementById('account-popup') as HTMLElement}
      >
        <div>
          {blockUblockWithdrawalsError && (
            <div className={`${cn.accountError} ${cn.blockError}`}>
              {blockUblockWithdrawalsError}
            </div>
          )}
        </div>
      </Popup>
      <Popup
        title={`${
          !creditBlock ? 'Unblock' : 'Block'
        } the top-up of this account`}
        onCancelClick={() => setBlockTopupPopupVisible(false)}
        onOkClick={() =>
          onBlockTopup({
            id: info?.id,
            creditBlock: !info?.creditBlock,
          })
        }
        cancelButtonText="Cancel"
        okButtonText={
          blockTopupInProgress
            ? 'Loading...'
            : `Yes, ${!creditBlock ? 'Unblock' : 'Block'}`
        }
        isVisible={blockTopupPopupVisible}
        parent={document.getElementById('account-popup') as HTMLElement}
      >
        <div>
          {blockUblockTopupError && (
            <div className={`${cn.accountError} ${cn.blockError}`}>
              {blockUblockTopupError}
            </div>
          )}
        </div>
      </Popup>
      <Popup
        title={`${closed ? 'Open' : 'Close'}  this account`}
        onCancelClick={() => {
          setClosePopupVisible(false);
          setComment('');
        }}
        isVisible={closePopupVisible}
        parent={document.getElementById('account-popup') as HTMLElement}
      >
        <>
          <div className={cn.commentWrapper}>
            <InputText
              name="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Enter comment"
            />
          </div>
          {closeOpenError && (
            <div className={cn.accountError}>{closeOpenError}</div>
          )}
          <div className={cn.commentControls}>
            <ButtonPseudo
              text="Cancel"
              onClick={() => {
                setClosePopupVisible(false);
                setComment('');
              }}
              bold
            />
            <ButtonPrimary
              text={
                closeInProgress
                  ? 'Loading...'
                  : `Yes, ${info?.closed ? 'open' : 'close'}`
              }
              onClick={() =>
                onClose({
                  id: info?.id,
                  comment,
                  closed: !info?.closed,
                })
              }
              disabled={closeInProgress || !comment}
              bold
            />
          </div>
        </>
      </Popup>
      <div id="account-popup" />
    </Card>
  );
};

export default AccountInfo;
